import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, type SilentRequest } from '@azure/msal-browser';
import { useCallback, useEffect, useRef } from 'react';
import { loginRequest, graphScopes, armScopes, cognitiveServicesScopes, dataPlaneScopes, storageScopes, resolveTenantId } from './msalConfig';
import { useMockAuth } from './MockAuthProvider';
import { telemetryService } from '@/services/telemetryService';
import { useAppStore } from '@/stores/appStore';
import { queryClient } from '@/providers/QueryProvider';
import { clearSidecarCache } from '@/services/nodesMetadataCache';
import { clearMockCache } from '@/services/mockDataCache';

const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';

/** Hash a string to a hex digest for PII-safe telemetry user IDs. */
async function hashUserId(id: string): Promise<string> {
  const data = new TextEncoder().encode(id);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Circuit breaker for token acquisition failures.
 * Prevents infinite retry loops when silent acquisition consistently fails.
 * Exported for testing.
 */
export const tokenFailureTracker = {
  failures: new Map<string, { count: number; lastFailure: number }>(),
  maxFailures: 3,
  resetWindowMs: 60_000, // Reset failure count after 1 minute of no failures
  
  recordFailure(key: string): number {
    const now = Date.now();
    const existing = this.failures.get(key);
    
    // Reset if enough time has passed
    if (existing && now - existing.lastFailure > this.resetWindowMs) {
      this.failures.delete(key);
    }
    
    const current = this.failures.get(key) ?? { count: 0, lastFailure: now };
    current.count++;
    current.lastFailure = now;
    this.failures.set(key, current);
    return current.count;
  },
  
  shouldBlock(key: string): boolean {
    const existing = this.failures.get(key);
    if (!existing) return false;
    
    // Reset if enough time has passed
    if (Date.now() - existing.lastFailure > this.resetWindowMs) {
      this.failures.delete(key);
      return false;
    }
    
    return existing.count >= this.maxFailures;
  },
  
  reset(key: string): void {
    this.failures.delete(key);
  },
  
  /** Clear all tracked failures. For testing only. */
  _clearAll(): void {
    this.failures.clear();
  }
};

/**
 * Detect tenant mismatch between the cached account and target tenant.
 * Returns true if there's a mismatch that would cause silent token acquisition to fail.
 * 
 * @param accountTenantId - The tenant ID from the cached MSAL account
 * @param targetTenantId - The tenant ID we're trying to acquire a token for
 * @returns true if there's a tenant mismatch
 */
export function hasTenantMismatch(
  accountTenantId: string | undefined,
  targetTenantId: string
): boolean {
  // No account tenant means no mismatch (account may not be set yet)
  if (!accountTenantId) {
    return false;
  }
  
  // 'organizations' is a multi-tenant wildcard that matches any tenant
  if (targetTenantId === 'organizations') {
    return false;
  }
  
  // Case-insensitive comparison of tenant IDs
  return accountTenantId.toLowerCase() !== targetTenantId.toLowerCase();
}

const useRealAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const telemetryUserSet = useRef(false);
  const prevTenantIdRef = useRef<string | null>(null);
  const forceRefreshRef = useRef(false);
  const { selectedTenantId } = useAppStore();

  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  // When tenant changes, mark all subsequent token requests for force-refresh
  // until a fresh token has been acquired
  if (prevTenantIdRef.current !== null && prevTenantIdRef.current !== selectedTenantId) {
    forceRefreshRef.current = true;
  }
  prevTenantIdRef.current = selectedTenantId;

  // Set/clear authenticated user context for telemetry
  useEffect(() => {
    if (isAuthenticated && account?.localAccountId && !telemetryUserSet.current) {
      telemetryUserSet.current = true;
      hashUserId(account.localAccountId).then((hashedId) => {
        telemetryService.setAuthenticatedUser(hashedId);
      }).catch(() => {
        telemetryUserSet.current = false;
      });
    } else if (!isAuthenticated && telemetryUserSet.current) {
      telemetryUserSet.current = false;
      telemetryService.clearAuthenticatedUser();
    }
  }, [isAuthenticated, account?.localAccountId]);

  const login = useCallback(async () => {
    try {
      telemetryService.trackEvent('auth.login.started');
      
      // Preserve an existing intended return URL (e.g., set by a ProtectedRoute)
      const existingReturnUrl = sessionStorage.getItem('authReturnUrl');
      if (!existingReturnUrl) {
        const { pathname, search, hash } = window.location;
        // Don't store /login as the return URL — after successful auth the
        // AuthProvider does `window.location.replace(returnUrl)`, which
        // would bounce the user back to /login (then LoginPage redirects
        // them to /, but that's a gratuitous round-trip and a visible flash).
        if (pathname !== '/login') {
          sessionStorage.setItem('authReturnUrl', `${pathname}${search}${hash}`);
        }
      }
      
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      const authError = error as { errorCode?: string; message?: string };
      telemetryService.trackEvent('auth.login.failed', {
        errorCode: authError.errorCode ?? 'unknown',
        errorMessage: authError.message ?? String(error),
      });
      console.error('[Auth] Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    telemetryService.trackEvent('auth.logout');
    telemetryService.clearAuthenticatedUser();
    
    // Flush all cached data to prevent stale state on next login
    queryClient.clear();
    // Drop the in-memory nodesMetadata sidecar cache too — it survives
    // re-renders and would otherwise carry one user's sidecar into the
    // next signed-in session on the same browser profile.
    clearSidecarCache();
    // Same for the draft-only mock sidecar cache.
    clearMockCache();

    // Clean up auth-related sessionStorage items
    sessionStorage.removeItem('authReturnUrl');
    
    await instance.logoutRedirect();
  }, [instance]);

  const getAccessToken = useCallback(
    async (scopes: string[], options?: { noRedirect?: boolean }): Promise<string | null> => {
      if (!account) {
        return null;
      }

      const noRedirect = options?.noRedirect ?? false;

      // Determine the target tenant - use selectedTenantId from store, fall back to configured tenant
      const targetTenantId = selectedTenantId ?? resolveTenantId();
      const accountTenantId = account.tenantId;
      const scopeKey = scopes.join(',');
      const failureKey = `${scopeKey}:${targetTenantId}`;
      
      // Track if there's a potential tenant mismatch (for telemetry, not blocking)
      const tenantMismatchDetected = hasTenantMismatch(accountTenantId, targetTenantId);

      // If circuit breaker is triggered, force interactive login immediately
      if (tokenFailureTracker.shouldBlock(failureKey)) {
        console.warn(
          `[Auth] Circuit breaker triggered for scopes: ${scopes.join(', ')}. ` +
          `Too many silent acquisition failures. Forcing interactive login.`
        );
        
        if (!noRedirect && inProgress === InteractionStatus.None) {
          try {
            const { pathname, search, hash } = window.location;
            sessionStorage.setItem('authReturnUrl', `${pathname}${search}${hash}`);
            
            telemetryService.trackEvent('auth.token.circuit_breaker_triggered', {
              scopes: scopeKey,
              targetTenantId,
              accountTenantId: accountTenantId ?? 'unknown',
            });

            await instance.acquireTokenRedirect({
              scopes,
              authority: `https://login.microsoftonline.com/${targetTenantId}`,
              prompt: 'select_account', // Force account selection to break out of loop
            });
            return null;
          } catch (redirectError) {
            console.error('[Auth] Circuit breaker redirect failed:', redirectError);
          }
        }
        return null;
      }

      const needsRefresh = forceRefreshRef.current;

      const request: SilentRequest = {
        scopes,
        account,
        // Use tenant-specific authority if a tenant is selected
        ...(selectedTenantId && { authority: `https://login.microsoftonline.com/${selectedTenantId}` }),
        // Force refresh to get a new token when tenant changes
        ...(needsRefresh && { forceRefresh: true }),
      };

      try {
        const response = await instance.acquireTokenSilent(request);
        // Clear force-refresh flag after first successful acquisition
        if (needsRefresh) forceRefreshRef.current = false;
        // Clear any failure tracking on success
        tokenFailureTracker.reset(failureKey);
        return response.accessToken;
      } catch (_error) {
        const authError = _error as { errorCode?: string; message?: string; errorMessage?: string };
        const errorCode = authError.errorCode ?? '';
        const errorMessage = authError.message ?? authError.errorMessage ?? '';
        
        // Track the failure
        const failureCount = tokenFailureTracker.recordFailure(failureKey);

        // Detect HTTP 400 errors from the error message (MSAL wraps these)
        // These typically indicate tenant mismatch or invalid token state
        const is400Error = errorMessage.includes('400') || 
          errorMessage.toLowerCase().includes('bad request') ||
          errorCode === 'invalid_grant' ||
          errorCode === 'bad_token_request';

        // Determine if we need interactive login
        // 400 errors are a strong signal that silent acquisition won't work
        const needsInteraction = 
          is400Error ||
          errorCode === 'interaction_required' ||
          errorCode === 'consent_required' ||
          errorCode === 'login_required' ||
          errorCode === 'token_renewal_error' ||
          // MSAL throws with no specific code when authority doesn't match cached tokens
          errorCode === '' || 
          errorCode === 'no_tokens_found';

        if (needsInteraction && !noRedirect && inProgress === InteractionStatus.None) {
          // Log tenant mismatch info if detected
          if (tenantMismatchDetected) {
            console.warn(
              `[Auth] Silent acquisition failed with potential tenant mismatch. ` +
              `Account tenant: ${accountTenantId}, Target tenant: ${targetTenantId}. ` +
              `Error: ${errorCode || 'unknown'} (${is400Error ? '400 error' : 'other'}). ` +
              `Initiating interactive login.`
            );
          }

          try {
            // Persist current URL so AuthRedirectPage returns here after redirect
            const { pathname, search, hash } = window.location;
            sessionStorage.setItem('authReturnUrl', `${pathname}${search}${hash}`);

            await instance.acquireTokenRedirect({
              scopes,
              authority: `https://login.microsoftonline.com/${targetTenantId}`,
              // Use select_account prompt for 400 errors to allow user to switch tenants
              ...(is400Error ? { prompt: 'select_account' as const } : {}),
              ...(account && !is400Error ? { account } : {}),
            });
            // acquireTokenRedirect navigates away — won't reach here
            return null;
          } catch (redirectError) {
            console.warn(`[Auth] Redirect to tenant ${targetTenantId} failed:`, redirectError);
          }
        }

        telemetryService.trackEvent('auth.token.refresh_failed', {
          scopes: scopes.join(','),
          tenantId: targetTenantId,
          accountTenantId: accountTenantId ?? 'unknown',
          errorCode,
          errorMessage: errorMessage.substring(0, 200), // Truncate for telemetry
          failureCount: String(failureCount),
          is400Error: String(is400Error),
          tenantMismatch: String(tenantMismatchDetected),
        });
        console.warn(
          `[Auth] Silent token acquisition failed for scopes: ${scopes.join(', ')}. ` +
          `Error: ${errorCode || 'unknown'}, Attempt: ${failureCount}/${tokenFailureTracker.maxFailures}. ` +
          (failureCount >= tokenFailureTracker.maxFailures - 1
            ? 'Next failure will trigger interactive login.'
            : 'Re-login may be required.')
        );
        return null;
      }
    },
    [instance, account, selectedTenantId, inProgress]
  );

  const getGraphToken = useCallback(async (): Promise<string | null> => {
    return getAccessToken(graphScopes.scopes);
  }, [getAccessToken]);

  const getArmToken = useCallback(async (): Promise<string | null> => {
    return getAccessToken(armScopes.scopes);
  }, [getAccessToken]);

  const getOttoToken = useCallback(async (): Promise<string | null> => {
    return getAccessToken(dataPlaneScopes.scopes);
  }, [getAccessToken]);

  const getFoundryToken = useCallback(async (): Promise<string | null> => {
    // noRedirect: the UI surfaces an "Authorize" button that runs an
    // interactive popup via grantFoundryAccess(). Falling back to a full-tab
    // redirect here would also trip the autosave beforeunload prompt for any
    // dirty workflow.
    return getAccessToken(cognitiveServicesScopes.scopes, { noRedirect: true });
  }, [getAccessToken]);

  const getStorageToken = useCallback(async (): Promise<string | null> => {
    return getAccessToken(storageScopes.scopes);
  }, [getAccessToken]);

  // Clear MSAL token cache - useful when switching tenants
  const clearTokenCache = useCallback(async (): Promise<void> => {
    try {
      await instance.clearCache();
    } catch (error) {
      console.warn('[Auth] Failed to clear token cache:', error);
    }
  }, [instance]);

  // Interactive consent for Foundry — must be called from a user gesture (click)
  const grantFoundryAccess = useCallback(async (): Promise<string | null> => {
    try {
      const response = await instance.acquireTokenPopup({
        scopes: cognitiveServicesScopes.scopes,
        ...(account ? { account } : {}),
      });
      // Reset circuit breaker so the next silent acquisition can use the
      // fresh cache entry written by the popup. Without this, prior silent
      // failures (3+) keep short-circuiting acquireTokenSilent and the
      // refetch after Authorize keeps failing with "Authentication required".
      const targetTenantId = selectedTenantId ?? resolveTenantId();
      tokenFailureTracker.reset(`${cognitiveServicesScopes.scopes.join(',')}:${targetTenantId}`);
      return response.accessToken;
    } catch (error) {
      console.warn('[Auth] Failed to acquire Foundry token:', error);
      return null;
    }
  }, [instance, account, selectedTenantId]);

  // Interactive consent for Graph API — must be called from a user gesture (click)
  const grantGraphAccess = useCallback(async (): Promise<string | null> => {
    try {
      const response = await instance.acquireTokenPopup({
        scopes: graphScopes.scopes,
        ...(account ? { account } : {}),
      });
      const targetTenantId = selectedTenantId ?? resolveTenantId();
      tokenFailureTracker.reset(`${graphScopes.scopes.join(',')}:${targetTenantId}`);
      return response.accessToken;
    } catch (error) {
      console.warn('[Auth] Failed to acquire Graph token:', error);
      return null;
    }
  }, [instance, account, selectedTenantId]);

  // Interactive consent for Storage API — must be called from a user gesture (click)
  const grantStorageAccess = useCallback(async (): Promise<string | null> => {
    try {
      const response = await instance.acquireTokenPopup({
        scopes: storageScopes.scopes,
        ...(account ? { account } : {}),
      });
      const targetTenantId = selectedTenantId ?? resolveTenantId();
      tokenFailureTracker.reset(`${storageScopes.scopes.join(',')}:${targetTenantId}`);
      return response.accessToken;
    } catch (error) {
      console.warn('[Auth] Failed to acquire Storage token:', error);
      return null;
    }
  }, [instance, account, selectedTenantId]);

  // Only show loading for initial auth flows (login, logout, redirect handling).
  // Include `Startup` so consumers don't see a brief `(isLoading=false,
  // isAuthenticated=false)` window during MSAL's initial state — which would
  // otherwise let them act on incomplete auth state (e.g. AuthRedirectPage
  // navigating to /login before handleRedirectPromise has consumed the code).
  // Exclude AcquireToken (popup/consent) since user is already authenticated and we don't want to unmount the UI.
  const isLoading = inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.Logout ||
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Startup;

  return {
    isAuthenticated,
    account,
    login,
    logout,
    isLoading,
    getAccessToken,
    getGraphToken,
    getArmToken,
    getOttoToken,
    getFoundryToken,
    getStorageToken,
    grantFoundryAccess,
    grantGraphAccess,
    grantStorageAccess,
    clearTokenCache,
  };
};

// Auth is always real MSAL — mock only for E2E tests.
// VITE_LOCAL_MODE controls runtime routing, not auth.
export const useAuth = isE2EMode ? useMockAuth : useRealAuth;
